import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle } from '../theme';

const SupplierScorecardPage = ({ API_BASE }) => {
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('spend');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        axios.get(`${API_BASE}/supplier-analytics/summary`).catch(() => ({ data: null })),
        axios.get(`${API_BASE}/supplier-analytics`).catch(() => ({ data: [] })),
      ]);
      setSummary(s.data);
      setSuppliers(list.data || []);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const stat = (label, val, color) => (
    <div style={{ ...cardStyle, textAlign:'center', minWidth:140 }}>
      <div style={{ fontSize:'0.72rem', color:theme.textMuted, fontWeight:600, textTransform:'uppercase' }}>{label}</div>
      <div style={{ fontSize:'1.6rem', fontWeight:700, color: color || theme.navy, marginTop:4 }}>{val}</div>
    </div>
  );

  const scoreColor = (pct) => {
    if (pct >= 90) return { bg:'#dcfce7', fg:'#166534' };
    if (pct >= 70) return { bg:'#e0f2fe', fg:'#075985' };
    if (pct >= 50) return { bg:'#fef3c7', fg:'#92400e' };
    return { bg:'#fee2e2', fg:'#991b1b' };
  };

  const sorted = [...suppliers].sort((a, b) => {
    const ak = a.kpi || {}, bk = b.kpi || {};
    if (sortBy === 'spend')   return (bk.totalSpend || 0) - (ak.totalSpend || 0);
    if (sortBy === 'onTime')  return (bk.onTimeRate || 0) - (ak.onTimeRate || 0);
    if (sortBy === 'fill')    return (bk.fillRate || 0) - (ak.fillRate || 0);
    if (sortBy === 'orders')  return (bk.totalOrders || 0) - (ak.totalOrders || 0);
    return 0;
  });

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 12px' }}>🏭 Supplier Performance Scorecard</h2>
      <div style={{ ...cardStyle, marginBottom:12, background:theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color:theme.navy, lineHeight:1.5 }}>
          <b>KPIs computed from your Purchase Orders:</b> on-time delivery %, fill rate (quantity received / ordered), total spend, avg order value. Use this to negotiate better terms or drop underperforming suppliers.
        </div>
      </div>

      {loading && <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>}

      {!loading && summary && (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
          {stat('Total Suppliers', summary.totalSuppliers ?? 0)}
          {stat('Active', summary.activeSuppliers ?? 0, theme.success)}
          {stat('Outstanding ₹', `₹${(summary.totalOutstanding || 0).toLocaleString('en-IN')}`, theme.warning)}
          {stat('Credit Limit ₹', `₹${(summary.totalCreditLimit || 0).toLocaleString('en-IN')}`)}
          {stat('Avg On-Time %', `${summary.avgOnTimeRate ?? 100}%`, (summary.avgOnTimeRate || 100) >= 85 ? theme.success : theme.warning)}
        </div>
      )}

      {!loading && (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
            <span style={{ fontSize:'0.85rem', fontWeight:600 }}>Sort by:</span>
            {[
              ['spend',  '💰 Spend'],
              ['onTime', '⏱️ On-Time'],
              ['fill',   '📦 Fill Rate'],
              ['orders', '🔢 Orders'],
            ].map(([k, lbl]) => (
              <button key={k} onClick={()=>setSortBy(k)}
                style={{ padding:'4px 12px', fontSize:'0.78rem', borderRadius:6, border:'1px solid #e5e7eb', cursor:'pointer',
                  background: sortBy===k ? theme.primary : 'white', color: sortBy===k ? 'white' : '#333', fontWeight:600 }}>
                {lbl}
              </button>
            ))}
          </div>

          <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
            {sorted.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No suppliers yet. Add suppliers in Pharmacy module first.</div> :
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Supplier</th>
                <th style={th}>Orders</th>
                <th style={th}>Total Spend</th>
                <th style={th}>Avg Order</th>
                <th style={th}>On-Time %</th>
                <th style={th}>Fill Rate %</th>
                <th style={th}>Open POs</th>
                <th style={th}>Last Order</th>
              </tr></thead>
              <tbody>
                {sorted.map(s => {
                  const k = s.kpi || {};
                  const ot = scoreColor(k.onTimeRate || 0);
                  const fr = scoreColor(k.fillRate || 0);
                  return (
                    <tr key={s._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                      <td style={{ ...td, fontWeight:600 }}>
                        {s.name}
                        {s.phone && <div style={{ fontSize:'0.72rem', color:'#666' }}>{s.phone}</div>}
                      </td>
                      <td style={td}>{k.totalOrders ?? 0}</td>
                      <td style={{ ...td, fontWeight:600 }}>₹{(k.totalSpend || 0).toLocaleString('en-IN')}</td>
                      <td style={td}>₹{(k.avgOrderValue || 0).toLocaleString('en-IN')}</td>
                      <td style={td}>
                        <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.75rem', fontWeight:700, background:ot.bg, color:ot.fg }}>
                          {k.onTimeRate ?? 100}%
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.75rem', fontWeight:700, background:fr.bg, color:fr.fg }}>
                          {k.fillRate ?? 100}%
                        </span>
                      </td>
                      <td style={{ ...td, color: (k.openOrders||0) > 0 ? theme.warning : '#333', fontWeight:600 }}>{k.openOrders ?? 0}</td>
                      <td style={{ ...td, fontSize:'0.8rem', color:'#666' }}>
                        {k.lastOrderAt ? new Date(k.lastOrderAt).toLocaleDateString('en-IN') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>}
          </div>

          {summary?.topSpend?.length > 0 && (
            <div style={{ ...cardStyle, marginTop:16 }}>
              <h4 style={{ margin:'0 0 10px', color:theme.navy }}>🏆 Top 10 by Spend</h4>
              {summary.topSpend.map((s, i) => (
                <div key={s.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f1f3f5', fontSize:'0.85rem' }}>
                  <span style={{ fontWeight:600 }}>{i+1}. {s.name}</span>
                  <span style={{ color:'#666' }}>{s.orders} orders · on-time {s.onTimeRate}%</span>
                  <span style={{ fontWeight:700 }}>₹{(s.spend || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };

export default SupplierScorecardPage;
